import { useAccountId } from '@labrav/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  PlanType,
  TextInputV2,
  RadioButtonsV2,
  RadioButtonGroupMapping,
  RadioButtonType,
  LabraToolTipV2,
  TextAreaV4,
} from '@labrav/react-components'
import React, { useMemo, useState } from 'react'
import { faList, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { FieldArray, FormikProps, useFormikContext } from 'formik'
import { get, isEmpty } from 'lodash'
import { useStyles } from './FlyOutProductDetailsForm.styles'

import { FileUploadWithLoader } from '@labrav/common'
import { DefaultNowType } from '../../../common/utils/dateTimeNow'
import { bucketName } from '../../../common/utils/constants'
import { mandatoryUsGovInTitleMessage } from '../../../common/utils/messagesConstants'
import { RequirementLink } from '../../../types/RequirementLink'
import { FormLockedType } from '../../../types/FormLockedType'
import {
  FlyoutListingProductType,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import { useSelector } from '../../../store/hooks'
import { getRandom } from '../../../common/utils/helperFunctions'
import {
  fileFormatIncorrectText,
  fileUploadButtonText,
  fileUploadContainerTitle,
  imageFileUploadDescription,
} from '../../../common/utils/productCreationUtil'
import clsx from 'clsx'
import { FlyoutProductDetailsFaqs } from './FlyoutProductDetailsFaqs'
import { getListingTextHighlights } from '../../../common/utils/listingTextRestrictions'

export type FlyOutProductDetailsFormProps = {
  now: DefaultNowType
  handleRefLinkClick?: (inex: number) => void
  detailView?: boolean
  detailViewTooltip?: string
} & RequirementLink &
  FormLockedType

export const FlyOutProductDetailsForm: React.FC<
  FlyOutProductDetailsFormProps
> = ({
  now,
  isFormLocked,
  handleRefLinkClick,
  detailView,
  detailViewTooltip,
}) => {
  const { classes } = useStyles()
  const accountId = useAccountId({ product: 'flyout' }) || ''
  const listingType = useSelector<FlyoutListingProductType>(
    state => state.flyoutProductInCreation.currentProductInCreation.listingType
  )
  const { values, errors, touched, handleBlur, handleChange, setFieldValue } =
    useFormikContext() as FormikProps<FlyoutProductSections>

  const generalInformation = values.generalInformation
  const generalInformationErrors = errors.generalInformation
  const generalInformationTouched = touched.generalInformation

  const highlights = generalInformation?.highlights || []
  const randomKey = useMemo(() => getRandom(), [highlights.length])

  const isReadOnlyValue = !!isFormLocked || !!detailView

  const isGovProductButtonOptionsV2: RadioButtonGroupMapping[] = [
    {
      key: 'Yes',
      label: 'Yes',
      type: RadioButtonType.STANDARD,
      disabled: isReadOnlyValue,
    },
    {
      key: 'No',
      label: 'No',
      type: RadioButtonType.STANDARD,
      disabled: isReadOnlyValue,
    },
  ]

  const isUserMigrated = listingType === FlyoutListingProductType.MIGRATED

  const updateProductLogoUrl = async (imageUrl: string) => {
    await setFieldValue('generalInformation.productLogoUrl', imageUrl)
    if (!isUserMigrated) {
      await setFieldValue('infoCollectionFormMetaData.productLogoUrl', imageUrl)
    }
  }
  const handleGovProductChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue(
      'generalInformation.isUsGovProduct',
      event.target.value === 'Yes'
    )
  }

  const handleChangeValue = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value as string
    await setFieldValue('generalInformation.title', value)
    await setFieldValue('productDetails.productName', value)
  }
  const deleteProductLogoUrl = async () => {
    await setFieldValue('generalInformation.productLogoUrl', '')
    if (!isUserMigrated) {
      await setFieldValue('infoCollectionFormMetaData.productLogoUrl', '')
    }
  }

  const [indexModal, setIndexModal] = useState<number>(0)

  return (
    <BoxV2
      heading={'Product details'}
      headingIcon={
        <FontAwesomeIcon
          icon={faList}
          style={{ width: '20px', height: '16px' }}
        />
      }
      secondaryActions={
        <FlyoutProductDetailsFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <>
        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Is the product you are listing here going to be offered
              exclusively in the AWS GovCloud (US) Regions?
            </Typography>
          </div>
          <div className={classes.textWidth}>
            <LabraToolTipV2
              followCursor={detailView}
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
            >
              <RadioButtonsV2
                name={'generalInformation.isGovCloud'}
                checkedValue={{
                  key: values.generalInformation.isUsGovProduct ? 'Yes' : 'No',
                  value: values.generalInformation.isUsGovProduct
                    ? 'Yes'
                    : 'No',
                }}
                handleChange={handleGovProductChange}
                radioButtonMappings={isGovProductButtonOptionsV2}
              />
            </LabraToolTipV2>
          </div>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Product title
            </Typography>
            <Typography className={classes.sectionText}>
              This is the name displayed to buyers at the top of the product
              listing page and in search results. Product should include
              GovCloud somewhere in the product title if the product is
              exclusively for GovCloud.{' '}
              <LabraToolTipV2 tooltipPlacement="bottom" label="">
                <ButtonV2
                  styleType="link"
                  onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
                >
                  (Refer no. 2 in image no. 1)
                </ButtonV2>
              </LabraToolTipV2>
            </Typography>
          </div>
          <div className={classes.textWidth}>
            <LabraToolTipV2
              followCursor={detailView}
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
            >
              <TextInputV2
                supportingText={
                  values.generalInformation.isUsGovProduct
                    ? mandatoryUsGovInTitleMessage
                    : ''
                }
                keyName="generalInformation.title"
                title="Enter a product title"
                type="text"
                placeHolder=""
                value={generalInformation?.title}
                isError={
                  !!(
                    generalInformationErrors?.title &&
                    generalInformationTouched?.title
                  )
                }
                maxCharacters={72}
                onChange={handleChangeValue}
                isReadOnly={isFormLocked as boolean}
                isRequired={false}
                errorText={
                  (!!generalInformationTouched?.title &&
                    generalInformationErrors?.title) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                updatedValue={generalInformation?.title}
                viewOnly={detailView}
                highlights={
                  generalInformationErrors?.title &&
                  generalInformationTouched?.title
                    ? getListingTextHighlights(
                        generalInformation?.title,
                        'plain'
                      )
                    : []
                }
              />
            </LabraToolTipV2>
          </div>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <div className={classes.sectionHeadingContainer}>
              {' '}
              <Typography className={classes.sectionHeading}>SKU</Typography>
              <Typography className={classes.sectionText}>Optional</Typography>
            </div>
            <Typography className={classes.sectionText}>
              This is a unique identifier you can assign to your product. This
              information is for your own tracking purposes and is not visible
              to buyers.
            </Typography>
          </div>
          <div className={classes.textWidth}>
            <LabraToolTipV2
              followCursor={detailView}
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
            >
              <TextInputV2
                keyName="generalInformation.sku"
                title="Enter a SKU"
                type="text"
                placeHolder=""
                value={generalInformation?.sku}
                updatedValue={generalInformation?.sku}
                isError={
                  !!(
                    generalInformationErrors?.sku &&
                    generalInformationTouched?.sku
                  )
                }
                maxCharacters={100}
                onChange={handleChange}
                isReadOnly={isFormLocked as boolean}
                isRequired={false}
                errorText={
                  (!!generalInformationTouched?.sku &&
                    generalInformationErrors?.sku) ||
                  ''
                }
                showSpinner={false}
                onBlur={handleBlur}
                viewOnly={detailView}
              />
            </LabraToolTipV2>
          </div>
        </div>

        <div
          className={classes.sectionContainer}
          data-testid="file-uploader-logo"
        >
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Provide a logo
            </Typography>
            <Typography className={classes.sectionText}>
              This will be uploaded to AWS Marketplace and displayed to buyers
              at the top of the product listing page and in search results.
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.uploadImageWidth}
          >
            <div
              className={classes.uploadImageWidth}
              id="generalInformation.productLogoUrl"
            >
              <FileUploadWithLoader
                keyName={'logo-upload-product-details'}
                partnerId={accountId}
                existingFileLinks={
                  isEmpty(generalInformation?.productLogoUrl)
                    ? []
                    : [generalInformation?.productLogoUrl]
                }
                assetType="logo_url"
                folder="product_logo_url"
                fileUploadDescription={imageFileUploadDescription}
                quantity={1}
                fileUploadType="png/jpg"
                unit="MB"
                uploadFileSize={5}
                acceptedFormats={['.png', '.jpg']}
                updateUrl={updateProductLogoUrl}
                deleteUrl={deleteProductLogoUrl}
                defaultNow={now}
                fileUploadButtonText={fileUploadButtonText}
                fileFormatIncorrectText={fileFormatIncorrectText}
                fileUploadContainerTitle={fileUploadContainerTitle}
                bucketName={bucketName}
                disabled={isReadOnlyValue}
                uploadError={
                  (!!generalInformationTouched?.productLogoUrl &&
                    generalInformationErrors?.productLogoUrl) ||
                  ''
                }
                viewOnly={detailView}
                checkDuplicate
                showRealFileName
              />
            </div>
          </LabraToolTipV2>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Short description
            </Typography>
            <Typography className={classes.sectionText}>
              Describe your product in 2–3 sentences, including any relevant
              keywords (single words or short phrases). This description is
              displayed to buyers at the top of the listing page under the
              product title. Descriptions over 188 characters in length require
              a ‘show more’ link to be viewed in full (up to 1,000 characters
              max).
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
              >
                (Refer no. 3 in image no. 1)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.fullWidth}
          >
            <TextAreaV4
              keyName="generalInformation.shortProductDescription"
              title="Enter a brief description of your product"
              rows={10}
              maxCharacters={1000}
              height={'100px'}
              type="string"
              placeHolder=""
              value={generalInformation?.shortProductDescription}
              updatedValue={generalInformation?.shortProductDescription}
              isError={
                !!(
                  generalInformationTouched?.shortProductDescription &&
                  generalInformationErrors?.shortProductDescription
                )
              }
              onChange={handleChange}
              isReadOnly={isFormLocked as boolean}
              isRequired={false}
              errorText={
                (!!generalInformationTouched?.shortProductDescription &&
                  generalInformationErrors?.shortProductDescription) ||
                ' '
              }
              showSpinner={false}
              onBlur={handleBlur}
              viewOnly={detailView}
              highlights={
                generalInformationTouched?.shortProductDescription &&
                generalInformationErrors?.shortProductDescription
                  ? getListingTextHighlights(
                      generalInformation?.shortProductDescription,
                      'plain'
                    )
                  : []
              }
            />
          </LabraToolTipV2>
        </div>

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Long description
            </Typography>
            <Typography className={classes.sectionText}>
              Describe the features and benefits of your product in 1–3
              paragraphs, including any relevant keywords (single words or short
              phrases). This description is displayed to buyers in the product
              overview section of the listing page and in search results. (up to
              5,000 characters max).
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
              >
                (Refer no. 4 in image no. 1)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.fullWidth}
          >
            <TextAreaV4
              keyName="generalInformation.productDescription"
              title="Enter a brief description of your product"
              rows={10}
              maxCharacters={5000}
              height={'240px'}
              type="string"
              placeHolder=""
              value={generalInformation?.productDescription}
              updatedValue={generalInformation?.productDescription}
              isError={
                !!(
                  generalInformationTouched?.productDescription &&
                  generalInformationErrors?.productDescription
                )
              }
              onChange={handleChange}
              isReadOnly={isFormLocked as boolean}
              isRequired={false}
              errorText={
                (!!generalInformationTouched?.productDescription &&
                  generalInformationErrors?.productDescription) ||
                ' '
              }
              showSpinner={false}
              onBlur={handleBlur}
              viewOnly={detailView}
              highlights={
                generalInformationTouched?.productDescription &&
                generalInformationErrors?.productDescription
                  ? getListingTextHighlights(
                      generalInformation?.productDescription,
                      'longDescription'
                    )
                  : []
              }
            />
          </LabraToolTipV2>
        </div>
        {values.productDetails.productType !==
          PlanType.PROFESSIONAL_SERVICES && (
          <div className={classes.sectionContainer}>
            <div className={classes.textContainer}>
              <div className={classes.sectionHeadingContainer}>
                {' '}
                <Typography className={classes.sectionHeading}>
                  Product video URL
                </Typography>
                <Typography className={classes.sectionText}>
                  Optional
                </Typography>
              </div>
              <Typography className={classes.sectionText}>
                Provide a link to video content promoting your product. A ‘See
                product video’ link will be displayed to buyers on the product
                listing page. The product video must be externally hosted and
                publicly available.
              </Typography>
            </div>
            <div className={classes.textWidth}>
              <LabraToolTipV2
                tooltipPlacement="bottom-start"
                description={detailViewTooltip}
                followCursor={detailView}
              >
                <TextInputV2
                  keyName="generalInformation.productVideoLink"
                  title="Enter product video URL"
                  type="text"
                  placeHolder=""
                  value={generalInformation?.productVideoLink}
                  updatedValue={generalInformation?.productVideoLink}
                  isError={
                    !!(
                      generalInformationErrors?.productVideoLink &&
                      generalInformationTouched?.productVideoLink
                    )
                  }
                  onChange={handleChange}
                  isReadOnly={isFormLocked as boolean}
                  isRequired={false}
                  errorText={
                    (!!generalInformationTouched?.productVideoLink &&
                      generalInformationErrors?.productVideoLink) ||
                    ''
                  }
                  showSpinner={false}
                  onBlur={handleBlur}
                  viewOnly={detailView}
                />
              </LabraToolTipV2>
            </div>
          </div>
        )}

        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Highlights
            </Typography>
            <Typography className={classes.sectionText}>
              This is a bulleted list of key features or selling points.. The
              Highlights section is displayed to buyers on the product listing
              page. For search optimization, we recommend incorporating 1–2
              relevant keywords used in your product descriptions. You can add
              up to 3 highlights (Each highlight can have up to 500 characters.){' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
              >
                (Refer no. 5 in image no. 1)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.fullWidth}
          >
            <FieldArray name="generalInformation.highlights">
              {({ push, remove }) => (
                <>
                  {highlights.map((support, index) => (
                    <div
                      className={classes.highlightRow}
                      data-testid="highlight-row"
                    >
                      <div
                        key={`highlight-${randomKey}-name`}
                        className={classes.highlighField}
                      >
                        <TextAreaV4
                          keyName={`generalInformation.highlights.${index}`}
                          title={`Highlight ${index + 1}`}
                          rows={10}
                          maxCharacters={500}
                          height={'100px'}
                          type="string"
                          placeHolder=""
                          value={support}
                          updatedValue={support}
                          isError={
                            !!(
                              get(
                                generalInformationTouched,
                                `highlights.${[index]}`,
                                false
                              ) &&
                              get(
                                generalInformationErrors,
                                `highlights.${[index]}`,
                                false
                              )
                            )
                          }
                          onChange={handleChange}
                          isReadOnly={isFormLocked as boolean}
                          isRequired={false}
                          errorText={
                            (!!get(
                              generalInformationTouched,
                              `highlights.${[index]}`,
                              false
                            ) &&
                              get(
                                generalInformationErrors,
                                `highlights.${[index]}`,
                                ' '
                              )) ||
                            ' '
                          }
                          showSpinner={false}
                          onBlur={handleBlur}
                          viewOnly={detailView}
                          highlights={
                            get(
                              generalInformationTouched,
                              `highlights.${[index]}`,
                              false
                            ) &&
                            get(
                              generalInformationErrors,
                              `highlights.${[index]}`,
                              false
                            )
                              ? getListingTextHighlights(support, 'plain')
                              : []
                          }
                        />
                      </div>
                      {highlights.length > 1 ? (
                        <div
                          className={clsx(
                            classes.trashIcon,
                            detailView ? classes.disableTrash : ''
                          )}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            style={{ width: '18px', height: '20px' }}
                            data-testid="trash-highlight"
                            onClick={() => {
                              remove(index)
                            }}
                          />
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  ))}
                  {highlights.length <= 2 ? (
                    <ButtonV2
                      styleType="link"
                      onClick={() => push('')}
                      data-testid="add-a-listing-button"
                      disabled={isReadOnlyValue}
                    >
                      Add a highlight
                    </ButtonV2>
                  ) : (
                    <></>
                  )}
                </>
              )}
            </FieldArray>
          </LabraToolTipV2>
        </div>
      </>
    </BoxV2>
  )
}
